import { editorStore } from '../../stores/editorStore';
import {
  LIGHTING_PRESETS,
  PARTICLE_PRESETS,
} from '../../types';

const EMOTIONS = ['happy', 'sad', 'curious', 'calm', 'fearful', 'surprised'];
const TEXTURES = ['soft', 'rough', 'shiny', 'fuzzy', 'crystalline'];
const PHYSICS = ['rigid', 'springy', 'floating', 'bouncy', 'heavy'];
const BEHAVIORS = ['idle', 'float', 'pulse', 'follow', 'sway', 'spin'];
const INTERACTIONS = ['none', 'tap', 'drag', 'shake', 'swipe'];

function VecInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: { x: number; y: number; z: number };
  onChange: (v: { x: number; y: number; z: number }) => void;
}) {
  return (
    <div className="editor-field-row">
      <span className="editor-field-label">{label}</span>
      {(['x', 'y', 'z'] as const).map((axis) => (
        <input
          key={axis}
          className="editor-vector-input"
          type="number"
          step={0.1}
          value={Number(value[axis].toFixed(2))}
          onChange={(e) => onChange({ ...value, [axis]: parseFloat(e.target.value) || 0 })}
        />
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="editor-section">
      <div className="editor-section-title">{title}</div>
      {children}
    </div>
  );
}

function ChipRow({
  options,
  value,
  onPick,
}: {
  options: string[];
  value: string;
  onPick: (v: string) => void;
}) {
  return (
    <div className="editor-chip-row">
      {options.map((opt) => (
        <button
          key={opt}
          className={`editor-chip${value === opt ? ' editor-chip-active' : ''}`}
          onClick={() => onPick(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

/** 右侧属性面板：元素属性 + 页面场景设置 */
export function PropertiesPanel() {
  const page = editorStore((s) =>
    s.book?.pages.find((p) => p.id === s.currentPageId) ?? null
  );
  const element = editorStore((s) =>
    page?.elements.find((e) => e.id === s.selectedElementId) ?? null
  );
  const book = editorStore((s) => s.book);

  const updateElement = editorStore((s) => s.updateElement);
  const updateNarrative = editorStore((s) => s.updateElementNarrative);
  const updateMetadata = editorStore((s) => s.updateMetadata);
  const removeElement = editorStore((s) => s.removeElement);
  const setPageDuration = editorStore((s) => s.setPageDuration);
  const updateText = editorStore((s) => s.updateText);
  const removeText = editorStore((s) => s.removeText);
  const addText = editorStore((s) => s.addText);

  if (!book || !page) {
    return <aside className="editor-properties editor-panel">载入中…</aside>;
  }

  return (
    <aside className="editor-properties editor-panel">
      <div className="editor-panel-header">
        <span className="editor-panel-title">属性</span>
        <span className="editor-panel-sub">当前页：{page.title}</span>
      </div>

      {/* 元素属性 */}
      {element ? (
        <>
          <Section title="基本信息">
            <div className="editor-field-row">
              <span className="editor-field-label">名称</span>
              <input
                className="editor-text-input"
                value={element.name}
                onChange={(e) => updateElement(element.id, { name: e.target.value })}
              />
            </div>
            <div className="editor-field-row">
              <span className="editor-field-label">分类</span>
              <span className="editor-readonly">{element.category}</span>
            </div>
            <label className="editor-inline">
              <input
                type="checkbox"
                checked={element.visible}
                onChange={(e) => updateElement(element.id, { visible: e.target.checked })}
              />
              可见
            </label>
          </Section>

          <Section title="位置 / 变换">
            <VecInput
              label="位置"
              value={element.position}
              onChange={(v) => updateElement(element.id, { position: v })}
            />
            <VecInput
              label="旋转"
              value={element.rotation}
              onChange={(v) => updateElement(element.id, { rotation: v })}
            />
            <VecInput
              label="缩放"
              value={element.scale}
              onChange={(v) => updateElement(element.id, { scale: v })}
            />
          </Section>

          <Section title="叙事元数据（NME）">
            <div className="editor-field-label">角色 {element.narrative?.role}</div>
            <ChipRow
              options={EMOTIONS}
              value={element.narrative?.emotion ?? 'calm'}
              onPick={(v) => updateNarrative(element.id, { ...element.narrative!, emotion: v })}
            />
            <div className="editor-field-label">质感</div>
            <ChipRow
              options={TEXTURES}
              value={element.narrative?.texture ?? 'soft'}
              onPick={(v) => updateNarrative(element.id, { ...element.narrative!, texture: v })}
            />
            <div className="editor-field-label">物理</div>
            <ChipRow
              options={PHYSICS}
              value={element.narrative?.physics ?? 'rigid'}
              onPick={(v) => updateNarrative(element.id, { ...element.narrative!, physics: v })}
            />
            <div className="editor-field-label">行为</div>
            <ChipRow
              options={BEHAVIORS}
              value={element.narrative?.behavior ?? 'idle'}
              onPick={(v) => updateNarrative(element.id, { ...element.narrative!, behavior: v })}
            />
            <div className="editor-field-label">交互</div>
            <ChipRow
              options={INTERACTIONS}
              value={element.narrative?.interaction ?? 'none'}
              onPick={(v) => updateNarrative(element.id, { ...element.narrative!, interaction: v })}
            />
            <div className="editor-field-row">
              <span className="editor-field-label">强度</span>
              <input
                className="editor-range"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={element.narrative?.intensity ?? 0.5}
                onChange={(e) =>
                  updateNarrative(element.id, {
                    ...element.narrative!,
                    intensity: parseFloat(e.target.value),
                  })
                }
              />
              <span className="editor-readonly">
                {Math.round((element.narrative?.intensity ?? 0.5) * 100)}%
              </span>
            </div>
          </Section>

          <Section title="视觉">
            <div className="editor-field-row">
              <span className="editor-field-label">色调</span>
              <input
                className="editor-color"
                type="color"
                value={element.color ?? '#5cc8ff'}
                onChange={(e) => updateElement(element.id, { color: e.target.value })}
              />
            </div>
            <div className="editor-field-row">
              <span className="editor-field-label">透明度</span>
              <input
                className="editor-range"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={element.opacity}
                onChange={(e) =>
                  updateElement(element.id, { opacity: parseFloat(e.target.value) })
                }
              />
            </div>
          </Section>

          <button className="editor-danger-btn" onClick={() => removeElement(element.id)}>
            删除元素
          </button>
        </>
      ) : (
        <>
          {/* 页面场景设置 */}
          <Section title="场景背景">
            <div className="editor-field-row">
              <span className="editor-field-label">背景色</span>
              <input
                className="editor-color"
                type="color"
                value={page.background.color}
                onChange={(e) =>
                  updateMetadata(page.id, 'background', { color: e.target.value })
                }
              />
            </div>
            <div className="editor-field-row">
              <span className="editor-field-label">场景类型</span>
              <span className="editor-readonly">{page.background.sceneType}</span>
            </div>
            <div className="editor-field-row">
              <span className="editor-field-label">时段</span>
              <span className="editor-readonly">{page.background.timeOfDay}</span>
            </div>
            <div className="editor-field-row">
              <span className="editor-field-label">情绪基调</span>
              <span className="editor-readonly">{page.background.mood}</span>
            </div>
          </Section>

          <Section title="光照预设">
            <div className="editor-preset-grid">
              {LIGHTING_PRESETS.map((p) => (
                <button
                  key={p.name}
                  className={`editor-preset-card${
                    page.lighting.preset === p.name ? ' editor-preset-card-active' : ''
                  }`}
                  onClick={() => updateMetadata(page.id, 'lighting', { preset: p.name })}
                >
                  <div
                    className="editor-preset-swatch"
                    style={{ background: `linear-gradient(135deg, ${p.ambient}, ${p.directional})` }}
                  />
                  <span>{p.label}</span>
                </button>
              ))}
            </div>
            <div className="editor-field-row">
              <span className="editor-field-label">强度</span>
              <input
                className="editor-range"
                type="range"
                min={0.2}
                max={2}
                step={0.1}
                value={page.lighting.intensity}
                onChange={(e) =>
                  updateMetadata(page.id, 'lighting', { intensity: parseFloat(e.target.value) })
                }
              />
              <span className="editor-readonly">{page.lighting.intensity.toFixed(1)}</span>
            </div>
            <label className="editor-inline">
              <input
                type="checkbox"
                checked={page.lighting.fog}
                onChange={(e) => updateMetadata(page.id, 'lighting', { fog: e.target.checked })}
              />
              雾效
            </label>
          </Section>

          <Section title="粒子预设">
            <div className="editor-preset-grid">
              {PARTICLE_PRESETS.map((p) => (
                <button
                  key={p.name}
                  className={`editor-preset-card${
                    page.particles.preset === p.name ? ' editor-preset-card-active' : ''
                  }`}
                  onClick={() => updateMetadata(page.id, 'particles', { preset: p.name })}
                >
                  <div
                    className="editor-preset-swatch"
                    style={{ background: p.color }}
                  />
                  <span>{p.label}</span>
                </button>
              ))}
            </div>
            <label className="editor-inline">
              <input
                type="checkbox"
                checked={page.particles.enabled}
                onChange={(e) =>
                  updateMetadata(page.id, 'particles', { enabled: e.target.checked })
                }
              />
              启用粒子
            </label>
          </Section>

          <Section title="页面时长">
            <div className="editor-field-row">
              <span className="editor-field-label">时长（秒）</span>
              <input
                className="editor-vector-input"
                type="number"
                min={1}
                max={120}
                value={page.duration}
                onChange={(e) =>
                  setPageDuration(page.id, Math.max(1, parseFloat(e.target.value) || 8))
                }
              />
            </div>
          </Section>

          {/* 文本层快速管理 */}
          <Section title="文本层（旁白/字幕）">
            <button className="editor-primary-btn" onClick={() => addText()}>
              + 添加文本
            </button>
            {page.texts.length === 0 && (
              <div className="editor-empty-hint">尚无文本，添加后可在右侧编辑</div>
            )}
            {page.texts.map((t) => (
              <div key={t.id} className="editor-text-item">
                <textarea
                  className="editor-textarea"
                  rows={2}
                  value={t.content}
                  onChange={(e) => updateText(t.id, { content: e.target.value })}
                />
                <div className="editor-text-item-row">
                  <input
                    className="editor-vector-input"
                    type="number"
                    step={1}
                    value={t.fontSize}
                    onChange={(e) => updateText(t.id, { fontSize: parseInt(e.target.value) || 20 })}
                  />
                  <input
                    className="editor-color"
                    type="color"
                    value={t.color}
                    onChange={(e) => updateText(t.id, { color: e.target.value })}
                  />
                  <label className="editor-inline">
                    <input
                      type="checkbox"
                      checked={t.bold}
                      onChange={(e) => updateText(t.id, { bold: e.target.checked })}
                    />
                    粗体
                  </label>
                  <button
                    className="editor-icon-btn"
                    title="删除"
                    onClick={() => removeText(t.id)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </Section>
        </>
      )}
    </aside>
  );
}